import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useAuthUserStore } from '../../user/authUserStore'

const isNavigatingState = ref(false)
const showOverlayState = ref(false)

const MIN_DISPLAY_MS = import.meta.env.VITE_MIN_LOADER_MS ?? 1000
const SHOW_DELAY_MS = 100

export function useNavigation() {
  const authUserStore = useAuthUserStore()
  const { isInitialised } = storeToRefs(authUserStore)

  const loadingMessage = computed(() => {
    if (!isInitialised.value) {
      return 'Logging In...'
    }

    return 'Loading...'
  })

  let navigationStartTime: number | null = null
  let showOverlayTimeout: ReturnType<typeof setTimeout> | null = null

  watch(isNavigatingState, (navigating) => {
    if (navigating) {
      showOverlayTimeout = setTimeout(() => {
        navigationStartTime = Date.now()
        showOverlayState.value = true
      }, SHOW_DELAY_MS)
    } else {
      if (showOverlayTimeout) {
        clearTimeout(showOverlayTimeout)
        showOverlayTimeout = null
      }

      if (!showOverlayState.value) {
        return
      }

      const elapsed = navigationStartTime ? Date.now() - navigationStartTime : 0
      const remainingTime = Math.max(MIN_DISPLAY_MS - elapsed, 0)

      if (remainingTime > 0) {
        setTimeout(() => {
          showOverlayState.value = false
          navigationStartTime = null
        }, remainingTime)
      } else {
        showOverlayState.value = false
        navigationStartTime = null
      }
    }
  })

  const isNavigating = computed(() => isNavigatingState.value)
  const showOverlay = computed(() => showOverlayState.value)

  const beginNavigation = () => {
    isNavigatingState.value = true
  }

  const endNavigation = () => {
    isNavigatingState.value = false
  }

  return {
    loadingMessage,
    isNavigating,
    showOverlay,
    beginNavigation,
    endNavigation,
  }
}
